import { loadApp, uuid, onUnmount, identifierAttribute, onWillMount, onMounted, getIframeUrl, createScopeName, defineCustomElement, customElementName } from '@ringcentral/mfe-core';
export { GlobalTransport, dynamicLoad, expose, getEntry, getGlobalTransport, getMeta, getStorageEntry, getStorageKey, globalTransport, onUpdateEntry, removeStorageEntry, removeStorageRegistry, satisfiesVersion, setStorageMode, setStorageRegistry, updateStorageEntry } from '@ringcentral/mfe-core';
import React, { useRef, useState, useEffect, useCallback, memo } from 'react';

/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
/**
 * Render app as React component
 *
 * Their rendering is isolated,
 * and any re-rendering of the parent component will not trigger an MFE re-render,
 * unless the parent component is unmounted and re-mounted.
 *
 * @example
 *
 * ```ts
 * import React from 'react';
 * import { render, screen, act } from '@testing-library/react';
 * import { useApp } from '@ringcentral/mfe-react';
 *
 * export default async () => {
 *   const App1 = () => {
 *     const App2 = useApp({
 *       name: '@example/app2',
 *       loader: () => global.mockImport('@example/app2'),
 *     });
 *     return <App2 />;
 *   };
 *   render(<App1 />);
 *   await act(() => Promise.resolve());
 *   expect(screen.queryByText('test')).toBeInTheDocument();
 * }
 * ```
 */
const useApp = ({ name, loader, props, attrs, bootstrap, }) => {
    const ModuleRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        loadApp(loader, name)
            .then((module) => {
            var _a;
            ModuleRef.current = module;
            if (typeof ((_a = ModuleRef.current) === null || _a === void 0 ? void 0 : _a.default) === 'undefined') {
                throw new Error(`[MFE] '${name}' app should export default exposed API.`);
            }
            // When using @testing-library/react, the state of the currently loaded module must not be updated until after the function has been executed,
            // otherwise the internal state of the component will not be updated when the component is rendered.
            setLoaded(true);
        })
            .catch((e) => {
            console.error(e);
            setLoaded(null);
        });
    }, []);
    const App = useCallback(memo((_props) => {
        const rootRef = useRef(null);
        const renderRef = useRef(null);
        const idRef = useRef(uuid(name));
        useEffect(() => {
            let callback;
            const { init, render } = ModuleRef.current.default;
            Promise.resolve().then(() => {
                const result = bootstrap
                    ? bootstrap(ModuleRef.current.default)
                    : init === null || init === void 0 ? void 0 : init();
                const _render = () => {
                    onWillMount({ target: rootRef.current, name, id: idRef.current });
                    callback = render(renderRef.current, Object.assign(Object.assign({}, props), _props), idRef.current);
                    onMounted({
                        target: rootRef.current,
                        name,
                        id: idRef.current,
                        type: "react-useApp" /* RenderType.App */,
                    });
                };
                if (result instanceof Promise) {
                    result.then(_render);
                    return;
                }
                _render();
            });
            return () => {
                onUnmount({ target: rootRef.current, name, id: idRef.current });
                callback === null || callback === void 0 ? void 0 : callback();
            };
        }, []);
        return (React.createElement("div", Object.assign({ ref: rootRef }, Object.assign(Object.assign({}, attrs), { [identifierAttribute]: idRef.current })),
            React.createElement("div", { ref: renderRef })));
    }), [loaded]);
    return loaded
        ? App
        : loaded === null
            ? (({ fallback: Fallback }) => Fallback ? React.createElement(Fallback, null) : null)
            : (({ loading: Loading }) => Loading ? React.createElement(Loading, null) : null);
};

/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
/* eslint-disable no-shadow */
/* eslint-disable jsx-a11y/iframe-has-title */
/**
 * Render app in iframe
 *
 * Their rendering is isolated,
 * and any re-rendering of the parent component will not trigger an MFE re-render,
 * unless the parent component is unmounted and re-mounted.
 *
 * @example
 *
 * ```ts
 * import React from 'react';
 * import { render, screen, act } from '@testing-library/react';
 * import { useIframe } from '@ringcentral/mfe-react';
 *
 * export default async () => {
 *   const App1 = () => {
 *     const App2 = useIframe({
 *       name: '@example/app2',
 *     });
 *     return <App2 />;
 *   };
 *   render(<App1 />);
 *   await act(() => Promise.resolve());
 *   expect(document.querySelector('iframe')!).toBeInTheDocument();
 * }
 * ```
 */
const useIframe = ({ name, url = '', attrs = {}, ref, scope, }) => {
    const Iframe = useCallback(memo(() => {
        const rootRef = useRef(null);
        const [iframeUrl, setIframeUrl] = useState(url);
        const idRef = useRef(uuid(name));
        if (Object.hasOwnProperty.call(attrs, 'src')) {
            console.warn(`[MFE] The iframe component of the site named "${name}" does not pass "src" props.`);
        }
        useEffect(() => {
            if (ref) {
                Object.assign(ref, {
                    current: rootRef.current,
                });
            }
            if (!iframeUrl) {
                getIframeUrl(name).then((_url) => {
                    setIframeUrl(_url);
                });
                onMounted({
                    target: rootRef.current,
                    name,
                    id: idRef.current,
                    type: "react-useIframe" /* RenderType.Iframe */,
                });
            }
            return () => {
                onUnmount({ target: rootRef.current, name, id: idRef.current });
            };
        }, []);
        const id = uuid(name);
        return (React.createElement("iframe", Object.assign({ ref: rootRef, frameBorder: "no" }, Object.assign(Object.assign({}, attrs), { [identifierAttribute]: id }), { src: iframeUrl, name: createScopeName(idRef.current, scope) })));
    }), [name]);
    return Iframe;
};

/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable guard-for-in */
/* eslint-disable @typescript-eslint/ban-types */
/**
 * Render app as Web Components
 *
 * Their rendering is isolated,
 * and any re-rendering of the parent component will not trigger an MFE re-render,
 * unless the parent component is unmounted and re-mounted.
 *
 * @example
 *
 * ```ts
 * import React from 'react';
 * import { render, screen, act } from '@testing-library/react';
 * import { useWebComponents } from '@ringcentral/mfe-react';
 *
 * export default async () => {
 *   const App1 = () => {
 *     const App2 = useWebComponents({
 *       name: '@example/app2',
 *       loader: () => global.mockImport('@example/app2'),
 *     });
 *     return <App2 />;
 *   };
 *   render(<App1 />);
 *   await act(() => Promise.resolve());
 *   expect(screen.queryByText('test')).toBeInTheDocument();
 * }
 * ```
 */
const useWebComponents = ({ name, shadowMode, useShadowDOM, loader, attrs, props, bootstrap, }) => {
    const ModuleRef = useRef(null);
    const idRef = useRef(uuid(name));
    const domRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        loadApp(loader, name)
            .then((module) => {
            var _a;
            ModuleRef.current = module;
            if (typeof ((_a = ModuleRef.current) === null || _a === void 0 ? void 0 : _a.default) === 'undefined') {
                throw new Error(`[MFE] '${name}' app should export default exposed API.`);
            }
            setLoaded(true);
        })
            .catch((e) => {
            console.error(e);
            setLoaded(null);
        });
    }, []);
    const App = useCallback(memo((_props) => {
        useEffect(() => {
            const { renderNode, injectedRoot } = defineCustomElement({
                name,
                shadowMode,
                useShadowDOM,
                customElement: domRef.current,
            });
            let callback;
            const { init, render } = ModuleRef.current.default;
            Promise.resolve().then(() => {
                const attributes = attrs !== null && attrs !== void 0 ? attrs : {};
                for (const key in attributes) {
                    renderNode.setAttribute(key, attributes[key]);
                }
                const result = bootstrap
                    ? bootstrap(ModuleRef.current.default)
                    : init === null || init === void 0 ? void 0 : init();
                const _render = () => {
                    onWillMount({ target: injectedRoot, name, id: idRef.current });
                    callback = render(renderNode, Object.assign(Object.assign({}, props), _props), idRef.current);
                    onMounted({
                        target: injectedRoot,
                        name,
                        id: idRef.current,
                        type: "react-useWebComponents" /* RenderType.WebComponents */,
                    });
                };
                if (result instanceof Promise) {
                    result.then(_render);
                    return;
                }
                _render();
            });
            return () => {
                onUnmount({ target: injectedRoot, name, id: idRef.current });
                callback === null || callback === void 0 ? void 0 : callback();
            };
        }, []);
        return React.createElement(customElementName, {
            ref: domRef,
            [identifierAttribute]: idRef.current,
        });
    }), [loaded]);
    return loaded
        ? App
        : loaded === null
            ? (({ fallback: Fallback }) => Fallback ? React.createElement(Fallback, null) : null)
            : (({ loading: Loading }) => Loading ? React.createElement(Loading, null) : null);
};

export { useApp, useIframe, useWebComponents };
//# sourceMappingURL=index.mjs.map
